import { FC } from 'react';
import { EditableDataTable, EditableColumn, Badge } from '@/components/ui';
import { Mail, Clock, AlertCircle, CheckCircle, SkipForward } from 'lucide-react';
import { SxmOutreachLog, OutreachLogStatus } from '../types';
import { formatDate } from '../utils/statusHelpers';

interface SxmOutreachTabProps {
  outreach: SxmOutreachLog[];
  loading?: boolean;
  error?: string | null;
  userId?: string;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  // Included to match expected props from Sales.tsx, though not used for read-only logs
  updateOutreach?: any;
  bulkUpdateOutreach?: any;
  deleteOutreach?: any;
  createOutreach?: any;
}

export const SxmOutreachTab: FC<SxmOutreachTabProps> = ({
  outreach,
  loading,
  error,
  pagination,
  onPaginationChange,
}) => {

  const getStatusBadge = (status: OutreachLogStatus) => {
    switch (status) {
      case OutreachLogStatus.SENT:
        return (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 flex items-center gap-1.5 py-1 px-3">
            <CheckCircle className="w-3 h-3" />
            Sent
          </Badge>
        );
      case OutreachLogStatus.FAILED:
        return (
          <Badge className="bg-rose-50 text-rose-700 border-rose-100 flex items-center gap-1.5 py-1 px-3">
            <AlertCircle className="w-3 h-3" />
            Failed
          </Badge>
        );
      case OutreachLogStatus.SKIPPED:
        return (
          <Badge className="bg-amber-50 text-amber-700 border-amber-100 flex items-center gap-1.5 py-1 px-3">
            <SkipForward className="w-3 h-3" />
            Skipped
          </Badge>
        );
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const columns: EditableColumn<SxmOutreachLog>[] = [
    {
      key: 'attemptNumber',
      header: 'Step',
      render: (item) => (
        <div className="flex flex-col items-center gap-1">
          <span className="font-extrabold text-slate-900 bg-slate-100 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] shadow-sm border border-slate-200">
            {item.attemptNumber}
          </span>
          <span className="text-[7px] text-slate-400 font-mono tracking-tighter uppercase" title={`Unique Log ID: ${item.id}`}>
            {item.id.slice(0, 4)}
          </span>
        </div>
      ),
    },
    {
      key: 'lead',
      header: 'Lead Details',
      render: (item) => {
        const getPlatformIcon = (platform?: string) => {
          const p = platform?.toLowerCase();
          if (p === 'linkedin') return <div className="p-1 bg-blue-50 rounded text-blue-600"><span className="text-[10px] font-bold">in</span></div>;
          if (p === 'facebook') return <div className="p-1 bg-blue-100 rounded text-blue-800"><span className="text-[10px] font-bold">fb</span></div>;
          if (p === 'instagram') return <div className="p-1 bg-pink-50 rounded text-pink-600"><span className="text-[10px] font-bold">ig</span></div>;
          if (p === 'twitter' || p === 'x') return <div className="p-1 bg-slate-100 rounded text-slate-900"><span className="text-[10px] font-bold">x</span></div>;
          return <div className="p-1 bg-slate-50 rounded text-slate-400"><Clock className="w-2 h-2" /></div>;
        };

        const name = item.lead?.fullName;
        const email = item.email || item.lead?.email;
        const company = item.lead?.companyName;

        return (
          <div className="flex flex-col gap-1 py-1">
            <div className="flex items-center gap-2">
              {getPlatformIcon(item.lead?.platform)}
              <span className="font-bold text-slate-900 truncate max-w-[180px]">
                {name || email || 'Captured Lead'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500 font-medium">
                {name ? email : (company || (email ? 'Direct Capture' : 'No Email'))}
              </span>
              {item.lead?.sourceUrl && (
                <a
                  href={item.lead.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 hover:bg-slate-100 rounded text-blue-400 transition-colors"
                >
                  <Mail className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'subject',
      header: 'Subject / Message',
      render: (item) => (
        <div className="flex flex-col max-w-md">
          <span className="text-[13px] font-medium text-slate-800 line-clamp-1">
            {item.subject || 'No Subject'}
          </span>
          <span className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
            <span className="bg-slate-50 border border-slate-100 px-1 rounded text-[9px] font-bold uppercase">Template</span>
            {item.templateUsed || 'Default'}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <div className="flex flex-col gap-1">
          {getStatusBadge(item.status)}
          {item.status === OutreachLogStatus.FAILED && item.errorMessage && (
            <span className="text-[9px] text-rose-500 font-medium max-w-[120px] line-clamp-1 italic" title={item.errorMessage}>
              Error: {item.errorMessage}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'sentAt',
      header: 'Activity Date',
      render: (item) => (
        <div className="flex items-center gap-2 text-slate-500 text-xs font-medium">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(item.sentAt)}
        </div>
      ),
    },
    {
      key: 'bookingLinkIncluded',
      header: 'Booking',
      render: (item) => (
        item.bookingLinkIncluded ? (
          <Badge variant="primary" className="text-[10px] font-bold">INCLUDED</Badge>
        ) : (
          <span className="text-[10px] text-slate-300 font-bold">N/A</span>
        )
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <div>
          <h2 className="text-xl font-bold text-slate-900 border-l-4 border-slate-900 pl-4">
            Outreach History
          </h2>
          <p className="text-[13px] text-slate-500 mt-1 font-medium pl-5">
            Real-time activity log of all automated engagement attempts.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 text-xs font-bold text-red-600 uppercase tracking-wider hover:text-red-800 border-b border-red-200 py-0.5"
            >
              System Retry
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white border border-slate-100 rounded-xl space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Accessing Logs...</p>
        </div>
      ) : outreach.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white border border-dashed border-slate-200 rounded-xl">
          <Mail className="w-12 h-12 text-slate-200 mb-4" />
          <h3 className="text-base font-bold text-slate-900">No Activity Logged</h3>
          <p className="text-[13px] text-slate-500 mt-1 font-medium italic">Waiting for the sequence to initialize...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <EditableDataTable
            columns={columns}
            data={outreach}
            keyExtractor={(item) => item.id}
            enableEditing={false}
            enableDelete={false}
            tableName="sxm_outreach_logs"
            pagination={pagination}
            onPaginationChange={onPaginationChange}
            manualPagination={!!onPaginationChange}
          />
        </div>
      )}
    </div>
  );
};
